import { forwardRef, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  IconButton,
  Popover,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useGlobalSearchStore } from "../stores/GlobalSearchStore";
import { KEYBOARD_SHORTCUTS } from "../constants/globalSearch.constants";

interface InputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  isLoading?: boolean;
}

const isMac =
  typeof navigator !== "undefined" &&
  navigator.platform.toUpperCase().indexOf("MAC") >= 0;

const GlobalSearchInput = forwardRef<HTMLInputElement, InputProps>(
  ({ value, onChange, onSubmit, isLoading = false }, ref) => {
    const { keywordOnly, setKeywordOnly } = useGlobalSearchStore();
    const [helpAnchor, setHelpAnchor] = useState<HTMLButtonElement | null>(
      null,
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && onSubmit) {
        e.preventDefault();
        onSubmit();
      }
    };

    const handleBlur = () => {
      if (onSubmit) {
        onSubmit();
      }
    };

    const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setHelpAnchor(event.currentTarget);
    };

    const handleHelpClose = () => {
      setHelpAnchor(null);
    };

    return (
      <Box sx={{ p: 2 }}>
        <TextField
          inputRef={ref}
          fullWidth
          placeholder="Search datasets and documents..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoComplete="off"
          autoFocus
          data-testid="global-search-input"
          data-attr="global-search:input:search-query"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {isLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    size="small"
                    onClick={handleHelpClick}
                    data-testid="global-search-help-button"
                    data-attr="global-search:btn:open-help"
                    sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
        <Popover
          open={Boolean(helpAnchor)}
          anchorEl={helpAnchor}
          onClose={handleHelpClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ p: 2, maxWidth: 280 }}>
            <Typography variant="subtitle2" gutterBottom>
              Search Help
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Search across all your datasets and document content instantly.
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 1 }}>
              <strong>Hybrid mode</strong> (default): Uses AI-powered semantic
              search combined with keyword matching to find relevant content.
            </Typography>
            <Typography variant="caption" component="div" sx={{ mb: 2 }}>
              <strong>Keyword only</strong>: Searches exact text matches in
              dataset names and descriptions.
            </Typography>
            <Typography variant="caption" color="text.disabled" component="div">
              <Box component="span" sx={{ display: "block" }}>
                {isMac
                  ? KEYBOARD_SHORTCUTS.toggle.mac
                  : KEYBOARD_SHORTCUTS.toggle.windows}{" "}
                Open/close search
              </Box>
              <Box component="span" sx={{ display: "block" }}>
                {KEYBOARD_SHORTCUTS.navigate} Navigate results
              </Box>
              <Box component="span" sx={{ display: "block" }}>
                {KEYBOARD_SHORTCUTS.select} Select result
              </Box>
              <Box component="span" sx={{ display: "block" }}>
                {KEYBOARD_SHORTCUTS.close} Close
              </Box>
            </Typography>
          </Box>
        </Popover>
        <Box
          sx={{
            mt: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={keywordOnly}
                onChange={(e) => setKeywordOnly(e.target.checked)}
                data-testid="global-search-keyword-only-toggle"
                data-attr="global-search:toggle:keyword-only"
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                Keyword matches only
              </Typography>
            }
          />
          <Typography variant="caption" color="text.disabled">
            {keywordOnly
              ? "Exact text matches in names"
              : "Hybrid search (semantic + keyword)"}
          </Typography>
        </Box>
      </Box>
    );
  },
);

GlobalSearchInput.displayName = "GlobalSearchInput";

export default GlobalSearchInput;
